import clsx from 'clsx';
import FocusTrap from 'focus-trap-react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { handleEscKeyDown } from '../../Accessibility/KeyboardEvents';
import { translation, TranslationsContext } from '../../Translations';
import CloseIcon from '../Cutesies/CloseIcon';

interface Props {
  close: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  customBackgroundCssClasses?: string;
  additionalClasses?: string;
  modalWidth?: string;
  contentPadding?: string;
  bodyPadding?: string;
  headerAction?: React.ReactNode;
  ariaLabel?: string;
  closeOnBackdropClick?: boolean;
  /**
   * Opt-out of the modal body's default `overflow-y-auto`. Use when the
   * modal's content is designed to always fit within the viewport (no
   * variable-height expansions). Prevents a spurious scrollbar from
   * appearing on OS/browser combos that render scrollbars for `auto`
   * regions even when nothing overflows. Default: false.
   */
  disableBodyScroll?: boolean;
}

class Modal extends React.Component<Props> {
  portal: HTMLElement;
  scrollbarWidth: number = 0;

  constructor(props: Props) {
    super(props);
    this.portal = document.createElement('div');
    this.portal.id = `${this.props.title} portal`;
    const root = document.getElementById('root');
    if (root) {
      root.appendChild(this.portal);
      // Calculate scrollbar width before hiding overflow
      this.scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      // Add padding to compensate for scrollbar removal
      if (this.scrollbarWidth > 0) {
        document.body.style.paddingRight = `${this.scrollbarWidth}px`;
      }
      document.body.classList.add('overflow-hidden');
    }
  }

  componentWillUnmount() {
    const root = document.getElementById('root');
    if (root && this.portal) {
      root.removeChild(this.portal);
      document.body.classList.remove('overflow-hidden');
      // Remove the padding compensation
      document.body.style.paddingRight = '';
    }
  }

  modalWidth = () =>
    this.props.modalWidth ? this.props.modalWidth : 'w-auto max-w-[600px] overflow-hidden';

  render() {
    const {
      close,
      title,
      children,
      customBackgroundCssClasses,
      additionalClasses,
      contentPadding,
      bodyPadding,
      headerAction,
      ariaLabel,
      closeOnBackdropClick = true,
      disableBodyScroll = false,
    } = this.props;
    return ReactDOM.createPortal(
      <TranslationsContext.Consumer>
        {(ts) => (
          <FocusTrap
            focusTrapOptions={{
              escapeDeactivates: false,
              fallbackFocus: '#modal-container',
              allowOutsideClick: true,
            }}
          >
            <div
              className="fixed inset-0 z-50 flex items-center justify-center text-[#212121]"
              data-test-modal={true}
              onKeyDown={handleEscKeyDown(close)}
              id="modal-container"
              tabIndex={-1}
              role="dialog"
              aria-modal={true}
              aria-label={ariaLabel || 'Modal'}
            >
              <div
                className={clsx(
                  `${this.modalWidth()} rounded-md bg-white`,
                  'relative z-[1] my-0 mx-5 lg:m-0',
                  'max-h-[calc(100vh-4rem)]',
                  bodyPadding ? bodyPadding : 'py-6 px-8',
                  // Use overflow-visible on BOTH axes when disableBodyScroll
                  // is on. `overflow-y-visible` alone doesn't work: browsers
                  // silently compute `overflow-y: visible` as `auto` when
                  // `overflow-x` is not also `visible` (CSS spec).
                  disableBodyScroll
                    ? 'overflow-visible'
                    : additionalClasses
                      ? additionalClasses
                      : 'overflow-hidden',
                  disableBodyScroll ? '' : 'overflow-y-auto',
                )}
                data-test-modal-body={true}
              >
                {headerAction ? (
                  <div className={clsx('float-right', bodyPadding ? 'px-8' : 'px-1')}>
                    {headerAction}
                  </div>
                ) : (
                  <div
                    className={clsx(
                      'border-0 bg-transparent text-2xl font-medium outline-none',
                      'float-right cursor-pointer pb-1 pt-4 opacity-80 hover:opacity-100',
                      bodyPadding ? 'px-8' : 'px-1',
                    )}
                    onClick={close}
                    data-test-close-button={true}
                    aria-label={translation('Close')(ts)}
                    role="button"
                  >
                    <CloseIcon />
                  </div>
                )}
                {title}
                <div
                  className={clsx(
                    disableBodyScroll ? 'overflow-visible' : 'overflow-auto',
                    'w-full py-0 font-normal text-black',
                    contentPadding ? contentPadding : 'px-4',
                  )}
                  data-test-modal-content={true}
                >
                  {children}
                </div>
              </div>
              <div
                className={clsx(
                  'absolute inset-0 bg-[#1f2229] opacity-75',
                  customBackgroundCssClasses,
                )}
                data-test-modal-shade={true}
                onClick={closeOnBackdropClick ? close : undefined}
              />
            </div>
          </FocusTrap>
        )}
      </TranslationsContext.Consumer>,
      this.portal,
    );
  }
}

export default Modal;
